import React, { useEffect, useState } from 'react';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import GenericSearchBar from '../../components/GenericSearchBar';
import Pagination from '../../components/shared/Pagination';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  currentUrl,
  isRenderingOnServer,
} from '../../helpers/projectConstant';
import {
  SearchResultDetailKnowledgebase,
  SearchResultKnowledgebase,
} from './IKnowledgebaseSearch';
import { Link } from '../../components/ui/Link';
import DefaultLayout from '../../layouts/DefaultLayout';
import Container from '../../components/ui/Container';
import SearchResult from './SearchResult';
import { Message } from '../../components/ui/Message';

export const KnowledgebaseSearchPage: React.FC<SearchResultKnowledgebase> = ({
  resultCount,
  resultDetails,
  pageNumber,
  pageSize,
  searchTerm,
}) => {
  const [currentPageData, setCurrentPageData] = useState<SearchResultDetailKnowledgebase[]>([]);
  const [initialSearch, setInitialSearch] = useState('');
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const routeUrl = '/kennisbank/zoekresultaat';
  const routeQuery = '?zoekTerm=';

  const breadCrumbItems = [
    { name: 'Home', linkTo: '/' },
    { name: 'Kennisbank', linkTo: `/kennisbank` },
    { name: 'Zoeken', linkTo: `${currentUrl}` },
  ];

  const handleSearchClick = (query: string): void => {
    const encodedQuery = encodeURI(query);
    window.location.href = `${routeUrl}${routeQuery}${encodedQuery}`;
  };
  const itemsPerPage = 10;
  const handlePageChange = (page: number) => {
    if ('URLSearchParams' in window) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('sorting.page', page.toString());
      window.location.search = searchParams.toString();
    }
  };

  if (isRenderingOnServer()) {
    const serverData = resultDetails?.slice(0, itemsPerPage - 1);
    currentPageData.push(...serverData);
  }

  useEffect(() => {
    setCurrentPageData(resultDetails?.slice(0, itemsPerPage - 1));

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const entries = urlParams.entries();

    let result = entries.next();
    while (!result.done) {
      const entry = result.value;
      if (entry[0] === 'zoekTerm') {
        setInitialSearch(decodeURI(entry[1]));
      }
      result = entries.next();
    }
    setIsDataLoaded(true);
  }, [resultDetails]);

  return (
    <DefaultLayout breadcrumbs={<Breadcrumbs links={breadCrumbItems} />}>
      <Container state="defaultLayoutFirst" className="pb-16 md:pb-24">
        <div className="w-full flex justify-center lg:w-fit">
          {isDataLoaded && (
            <GenericSearchBar
              searchClicked={handleSearchClick}
              initialValue={initialSearch}
              title={'Zoeken op trefwoord'}
              placeholder={'Zoeken op onderwerp'}
            />
          )}
        </div>

        <div className="flex flex-row flex-wrap lg:-mx-2.5">
          <div className="w-full lg:w-[calc(66.6666%-1.25rem)] lg:mx-2.5">
            <h1 className="text-xl font-bold text-blue-700 font-avantgarde my-8">
              <span className="text-blue-500">{resultCount}</span>
              <span className="px-2">{`${resultCount !== 1 ? 'resultaten' : 'resultaat'} gevonden voor`}</span>
              <span className="font-normal">“{initialSearch}”</span>
            </h1>

            <div>
              {currentPageData.length > 0 ? (
                <>
                  <ul className="mt-2 font-normal text-sm">
                    {currentPageData?.map((item) => (
                      <SearchResult {...item} key={item.id} />
                    ))}
                  </ul>
                  {resultDetails?.length !== 0 && (
                    <div className="my-5">
                      <Pagination
                        itemsPerPage={pageSize}
                        totalItems={resultCount}
                        onPageChange={handlePageChange}
                        initialPage={pageNumber}
                        urlPath={routeUrl}
                        urlQuery={!!searchTerm ? `${routeQuery}${searchTerm}` : ''}
                        pageQueryName='sorting.page'
                      />
                    </div>
                  )}
                </>
              ) : (
                <Message className="mb-4">
                  Er zijn geen zoekresultaten gevonden voor uw zoekopdracht <span className="font-bold">"{initialSearch}"</span>
                </Message>
              )}
            </div>
          </div>

          <div className="w-full lg:w-[calc(33.3333%-1.25rem)] lg:mx-2.5 lg:pl-8 lg:mt-[6rem]">
            <div className="sticky top-24 bg-stone-50 text-center w-full px-5 py-10 rounded-[12px] margin-0 auto">
              <h3 className="font-avantgarde font-bold text-lg md:text-2xl text-blue-700 mb-4 text-center">
                Staat uw vraag er niet tussen?
              </h3>
              <div className="w-full flex justify-center">
                <Link
                  title="Stel een vraag"
                  variant="primary"
                  href="/expert/steleenvraag"
                  className="mt-[34px] w-full md:w-fit md:text-sm font-bold"
                >
                  Stel een vraag
                  <FontAwesomeIcon icon={faChevronRight as IconProp} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </DefaultLayout>
  );
};

KnowledgebaseSearchPage.displayName = "Page:KnowledgebaseSearch";

export default KnowledgebaseSearchPage;